import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { MsalProvider, useMsal, useAccount } from '@azure/msal-react';
import { msalConfig, loginRequest, isEnterpriseAccount, extractOrganizationData } from '../authConfig';
import { onConcurrentSession, markSessionReady, resetSessionState } from '../api/authFetch';

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize and handle redirect response on page load
msalInstance.initialize().then(() => {
  // Handle redirect response (from loginRedirect flow)
  msalInstance.handleRedirectPromise().then((response) => {
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  }).catch((err) => {
    // On timeout or interaction_in_progress, clear ALL stale MSAL interaction
    // state so the next login attempt doesn't get stuck.  MSAL stores multiple
    // keys; the set below covers @azure/msal-browser v2/v3.
    const errorCode = (err as any)?.errorCode || '';
    if (errorCode === 'timed_out' || errorCode === 'interaction_in_progress') {
      console.warn('MSAL redirect handling timed out — clearing interaction state for retry');
      const keysToRemove = Object.keys(sessionStorage).filter(
        (k) => k.startsWith('msal.') || k.includes('interaction'),
      );
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } else {
      console.error('Redirect handling error:', err);
    }
  });
}).catch((err) => {
  console.warn('MSAL initialization skipped (Azure AD not configured):', err.message || err);
});

// Constants for timeout functionality
const TIMEOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_DURATION = 2 * 60 * 1000; // 2 minutes warning before timeout
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

// Authentication context type
interface AuthContextType {
  isAuthenticated: boolean;
  isEnterpriseUser: boolean;
  user: AccountInfo | null;
  organizationData: any | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  devLogin: (email: string, password: string) => Promise<void>;
  isDevMode: boolean;
  loading: boolean;
  error: string | null;
  timeoutWarning: boolean;
  timeRemaining: number;
  extendSession: () => void;
  getAccessToken: () => Promise<string | null>;
  concurrentSessionDetected: boolean;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider>
        {children}
      </AuthContextProvider>
    </MsalProvider>
  );
};

// Internal context provider
const AuthContextProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devMode, setDevMode] = useState(() => sessionStorage.getItem('dev_mode') === 'true');
  const [organizationData, setOrganizationData] = useState<any | null>(null);
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [concurrentSessionDetected, setConcurrentSessionDetected] = useState(false);
  
  // Refs for timeout management
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const isAuthenticated = devMode || !!account;
  const isEnterpriseUser = devMode || (account ? isEnterpriseAccount(account) : false);

  // Register concurrent session callback so authFetch can trigger forced logout
  useEffect(() => {
    onConcurrentSession(() => {
      setConcurrentSessionDetected(true);
      // Clear session state — user must re-authenticate
      localStorage.removeItem('session_token');
    });
  }, []);

  // Restore dev-mode organizationData on page reload
  useEffect(() => {
    if (devMode && !organizationData) {
      const devToken = sessionStorage.getItem('dev_token');
      const sessToken = localStorage.getItem('session_token');
      if (devToken) {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const headers: Record<string, string> = { Authorization: `Bearer ${devToken}` };
        if (sessToken) headers['X-Session-Token'] = sessToken;
        fetch(`${apiBase}/api/auth/me`, { headers })
          .then((res) => res.json())
          .then((data) => {
            if (data.success && data.user) {
              setOrganizationData({
                organizationName: data.user.organization_name || 'Dev Organization',
                organizationDomain: data.user.organization_domain || 'kpmg.com',
                userEmail: data.user.email,
                userName: data.user.name,
              });
            }
          })
          .catch(() => {});
      }
    }
  }, [devMode, organizationData]);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setTimeoutWarning(false);
    setTimeRemaining(0);
    
    // Clear existing timers
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (warningTimeoutIdRef.current) {
      clearTimeout(warningTimeoutIdRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    // Only set timers if user is authenticated
    if (isAuthenticated && isEnterpriseUser) {
      // Set warning timer (13 minutes - 2 minutes before timeout)
      warningTimeoutIdRef.current = setTimeout(() => {
        setTimeoutWarning(true);
        setTimeRemaining(WARNING_DURATION);
        
        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
          setTimeRemaining((prev) => {
            const newTime = prev - 1000;
            if (newTime <= 0) {
              // Auto logout — handle dev mode gracefully
              const isDevLogin = sessionStorage.getItem('dev_mode') === 'true';
              if (isDevLogin) {
                sessionStorage.removeItem('dev_mode');
                sessionStorage.removeItem('dev_token');
                window.location.reload();
              } else {
                instance.logoutRedirect({
                  postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
                }).catch(console.error);
              }
              return 0;
            }
            return newTime;
          });
        }, 1000);
      }, TIMEOUT_DURATION - WARNING_DURATION);
      
      // Set automatic logout timer (15 minutes)
      timeoutIdRef.current = setTimeout(() => {
        console.log('Session timeout - automatically logging out user');
        const isDevLogin = sessionStorage.getItem('dev_mode') === 'true';
        if (isDevLogin) {
          sessionStorage.removeItem('dev_mode');
          sessionStorage.removeItem('dev_token');
          window.location.reload();
        } else {
          instance.logoutRedirect({
            postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
          }).catch(console.error);
        }
      }, TIMEOUT_DURATION);
    }
  }, [isAuthenticated, isEnterpriseUser, instance]);

  // Extend session function
  const extendSession = useCallback(() => {
    if (isAuthenticated) {
      resetActivityTimer();
    }
  }, [isAuthenticated, resetActivityTimer]);

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated || !isEnterpriseUser) {
      return;
    }

    const handleActivity = () => {
      const now = Date.now();
      // Only reset if it's been more than 30 seconds since last activity (debounce)
      if (now - lastActivityRef.current > 30000) {
        resetActivityTimer();
      }
    };

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(eventType => {
      document.addEventListener(eventType, handleActivity, true);
    });

    // Initial timer setup
    resetActivityTimer();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(eventType => {
        document.removeEventListener(eventType, handleActivity, true);
      });
      
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [isAuthenticated, isEnterpriseUser, resetActivityTimer]);

  useEffect(() => {
    if (account && isEnterpriseUser) {
      const orgData = extractOrganizationData(account);
      setOrganizationData(orgData);
    } else if (!devMode) {
      // Only clear org data when NOT in dev mode (dev login sets it separately)
      setOrganizationData(null);
      // Clear timeout states when not authenticated
      setTimeoutWarning(false);
      setTimeRemaining(0);
      
      // Clear all timers
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
        warningTimeoutIdRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [account, isEnterpriseUser, devMode]);

  const login = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      // Use redirect flow — faster on corporate networks with SSO/MFA
      await instance.loginRedirect(loginRequest);
      // Page will redirect and return; handleRedirectPromise handles the response
    } catch (error: any) {
      const errorCode = error?.errorCode || '';
      if (errorCode === 'interaction_in_progress' || errorCode === 'timed_out') {
        // Clear ALL stale interaction state and retry once
        const keysToRemove = Object.keys(sessionStorage).filter(
          (k) => k.startsWith('msal.') || k.includes('interaction'),
        );
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
        try {
          await instance.loginRedirect(loginRequest);
          return;
        } catch (retryErr: any) {
          console.error('Login retry failed:', retryErr);
          setError(retryErr.message || 'Login failed — please refresh and try again');
        }
      } else {
        console.error('Login failed:', error);
        setError(error.message || 'Login failed');
      }
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      // Check dev mode BEFORE clearing sessionStorage
      const wasDevMode = sessionStorage.getItem('dev_mode') === 'true';

      // Notify backend to invalidate session (best-effort)
      try {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const authToken = sessionStorage.getItem('dev_token');
        const sessToken = localStorage.getItem('session_token');
        const headers: Record<string, string> = {};
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
        if (sessToken) headers['X-Session-Token'] = sessToken;
        await fetch(`${apiBase}/api/auth/logout`, { method: 'POST', headers });
      } catch { /* best-effort */ }

      // Clear dev mode
      sessionStorage.removeItem('dev_mode');
      sessionStorage.removeItem('dev_token');
      localStorage.removeItem('session_token');
      sessionStorage.removeItem('portal_redirected');
      resetSessionState();
      setConcurrentSessionDetected(false);
      setDevMode(false);

      // Clear timeout states
      setTimeoutWarning(false);
      setTimeRemaining(0);
      
      // Clear all timers
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
        warningTimeoutIdRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      
      // For dev mode, just clear state and reload — no MSAL redirect
      if (wasDevMode) {
        setOrganizationData(null);
        setLoading(false);
        window.location.reload();
        return;
      }

      await instance.logoutRedirect({
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
      setOrganizationData(null);
    } catch (error: any) {
      console.error('Logout failed:', error);
      setError(error.message || 'Logout failed');
    } finally {
      setLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const silentRequest = {
        scopes: loginRequest.scopes,
        account: account
      };

      const response = await instance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (error: any) {
      console.error('Failed to acquire access token:', error);
      
      // If silent token acquisition fails, redirect to re-authenticate
      try {
        await instance.acquireTokenRedirect(loginRequest);
        // Page will redirect; return null as flow continues after redirect
        return null;
      } catch (interactiveError: any) {
        console.error('Interactive token acquisition also failed:', interactiveError);
        return null;
      }
    }
  };

  const devLogin = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(`${apiBase}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Dev login failed');
      }
      sessionStorage.setItem('dev_mode', 'true');
      sessionStorage.setItem('dev_token', data.token);
      // Store session token for single-session enforcement
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
        markSessionReady();
      }

      // Fetch full profile so organizationData is populated for dev users
      try {
        const meRes = await fetch(`${apiBase}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${data.token}`,
            ...(data.session_token ? { 'X-Session-Token': data.session_token } : {}),
          },
        });
        const meData = await meRes.json();
        if (meData.success && meData.user) {
          // If /me returned a new session_token, prefer it
          if (meData.session_token) {
            localStorage.setItem('session_token', meData.session_token);
          }
          setOrganizationData({
            organizationName: meData.user.organization_name || 'Dev Organization',
            organizationDomain: meData.user.organization_domain || 'kpmg.com',
            userEmail: meData.user.email,
            userName: meData.user.name,
          });
        }
      } catch {
        // Profile fetch failed — set minimal data from login response
        setOrganizationData({
          organizationName: 'Dev Organization',
          organizationDomain: 'kpmg.com',
          userEmail: data.user?.email || email,
          userName: data.user?.name || 'Dev User',
        });
      }

      setDevMode(true);
    } catch (err: any) {
      setError(err.message || 'Dev login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isEnterpriseUser,
    user: account,
    organizationData,
    login,
    logout,
    devLogin,
    isDevMode: devMode,
    loading,
    error,
    timeoutWarning,
    timeRemaining,
    extendSession,
    getAccessToken,
    concurrentSessionDetected,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
